import { canonicalJson } from './hash.ts';
import { FORECAST_DEFAULTS, type Resolution } from './forecast.ts';

/**
 * Deep links from a chat result to the same view on the web. Every link names the plan, its
 * version, the as-of day and the latest waypoint, so the page can tell whether it still shows
 * what the chat said; `h` is the result hash where the page can recompute it.
 */
export interface LinkStamp {
  readonly planId: string;
  readonly version: number;
  readonly asOf: string;
  readonly waypointId: string | null;
  readonly hash?: string;
}

/** Longest `c` (what-if change set) a link carries; longer change sets are left out of the link. */
export const MAX_CHANGES_PARAM = 1500;

function query(pairs: readonly (readonly [string, string | number | null | undefined])[]): string {
  const params = new URLSearchParams();
  for (const [k, v] of pairs) if (v !== null && v !== undefined && v !== '') params.append(k, String(v));
  return params.toString();
}

const stampPairs = (s: LinkStamp, withHash: boolean) =>
  [['v', s.version], ...(withHash ? [['h', s.hash] as const] : []), ['asof', s.asOf], ['w', s.waypointId], ['from', 'chat']] as const;

export const overviewPath = (s: LinkStamp): string => `/?${query([['plan', s.planId], ...stampPairs(s, true)])}`;

export function forecastPath(s: LinkStamp, options: { years: number; resolution?: Resolution; maxPoints?: number }): string {
  const resolution = options.resolution ?? FORECAST_DEFAULTS.resolution;
  const maxPoints = options.maxPoints ?? FORECAST_DEFAULTS.maxPoints;
  return `/forecast?${query([
    ['plan', s.planId],
    ['years', options.years],
    ...(resolution !== FORECAST_DEFAULTS.resolution ? [['res', resolution] as const] : []),
    ...(maxPoints !== FORECAST_DEFAULTS.maxPoints ? [['max', maxPoints] as const] : []),
    ...stampPairs(s, true),
  ])}`;
}

export const goalsPath = (s: LinkStamp): string => `/goals?${query([['plan', s.planId], ...stampPairs(s, true)])}`;

const toBase64Url = (text: string): string => {
  let binary = '';
  for (const byte of new TextEncoder().encode(text)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (value: string): string => {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
};

export const encodeChanges = (changes: unknown): string => toBase64Url(canonicalJson(changes));

export function decodeChanges(value: string): unknown {
  try {
    return JSON.parse(fromBase64Url(value)) as unknown;
  } catch {
    return null;
  }
}

/** The what-if link; `changesInLink` is false when the change set was too long to carry. */
export function whatIfPath(s: LinkStamp, changes: unknown): { path: string; changesInLink: boolean } {
  const c = encodeChanges(changes);
  const fits = c.length <= MAX_CHANGES_PARAM;
  return { path: `/what-if?${query([['plan', s.planId], ...stampPairs(s, false), ['c', fits ? c : null]])}`, changesInLink: fits };
}

export const affordabilityPath = (s: LinkStamp, price: string, item: string): string =>
  `/what-if?${query([['plan', s.planId], ['buy', price], ['item', item], ...stampPairs(s, false)])}`;

export const webUrl = (appOrigin: string, path: string): string => `${appOrigin.replace(/\/+$/, '')}${path}`;

/** What a chat link asks the web page to show and check. */
export interface ChatLink {
  readonly fromChat: boolean;
  readonly planId: string | null;
  readonly version: number | null;
  readonly hash: string | null;
  readonly asOf: string | null;
  readonly waypointId: string | null;
  readonly years: number | null;
  readonly resolution: Resolution;
  readonly maxPoints: number;
  readonly changes: unknown;
  readonly buy: string | null;
  readonly item: string | null;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const HASH = /^[0-9a-f]{8}$/;

export function parseChatLink(search: string | URLSearchParams): ChatLink {
  const p = typeof search === 'string' ? new URLSearchParams(search) : search;
  const int = (key: string, min: number, max: number): number | null => {
    const raw = p.get(key);
    if (raw === null || !/^\d+$/.test(raw)) return null;
    const n = Number(raw);
    return n >= min && n <= max ? n : null;
  };
  const asOf = p.get('asof');
  const hash = p.get('h');
  const c = p.get('c');
  const buy = p.get('buy');
  return {
    fromChat: p.get('from') === 'chat',
    planId: p.get('plan'),
    version: int('v', 0, Number.MAX_SAFE_INTEGER),
    hash: hash !== null && HASH.test(hash) ? hash : null,
    asOf: asOf !== null && DAY.test(asOf) ? asOf : null,
    waypointId: p.get('w'),
    years: int('years', 1, 50),
    resolution: p.get('res') === 'monthly' ? 'monthly' : 'yearly',
    maxPoints: int('max', 2, 600) ?? FORECAST_DEFAULTS.maxPoints,
    changes: c === null ? null : decodeChanges(c),
    buy: buy !== null && /^\d+(\.\d{1,2})?$/.test(buy) ? buy : null,
    item: p.get('item'),
  };
}

export type LinkCheck =
  | { readonly status: 'same' }
  | { readonly status: 'version_changed'; readonly current: number }
  | { readonly status: 'waypoint_changed' }
  | { readonly status: 'different' }
  /** Same version and waypoint, and the link carries no hash to compare (what-if, affordability). */
  | { readonly status: 'same_version' }
  | { readonly status: 'unchecked' };

/**
 * Compares a chat link with what the page computes now. `recomputed` is the hash of the same view
 * at the link's as-of day; at the same plan version and waypoint it must equal the link's hash, so
 * `different` is a parity failure, never an expected state.
 */
export function checkLink(link: ChatLink, current: { version: number; waypointId: string | null; recomputed: string | null }): LinkCheck {
  if (link.version === null) return { status: 'unchecked' };
  if (link.version !== current.version) return { status: 'version_changed', current: current.version };
  if ((link.waypointId ?? null) !== current.waypointId) return { status: 'waypoint_changed' };
  if (link.hash === null) return { status: 'same_version' };
  if (current.recomputed === null) return { status: 'unchecked' };
  return link.hash === current.recomputed ? { status: 'same' } : { status: 'different' };
}
